import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "@/lib/utils";
import { ChangeHandler } from "react-hook-form";

interface RadioGroupProps {
  children: React.ReactNode;
  className?: string;
  error?: string;
  onChange?: (value: string) => void;
  onBlur?: ChangeHandler;
  name?: string;
  value?: string;
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, error, onChange, onBlur, name, value, ...props }, ref) => {
  return (
    <div className="space-y-2">
      <RadioGroupPrimitive.Root
        className={cn("space-y-4", className)}
        onValueChange={onChange}
        value={value}
        name={name}
        {...props}
        ref={ref}
      />
      {error && <p className="text-sm text-system-error">{error}</p>}
    </div>
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  value: string;
  label: string;
  description?: string;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, value, label, description, ...props }, ref) => {
  const id = React.useId();

  return (
    <div className="flex items-start gap-4">
      <div className="flex items-start">
        <div className="mr-2 sm:mr-4">
          <RadioGroupPrimitive.Item
            ref={ref}
            id={id}
            value={value}
            className={cn(
              "mt-1 h-5 w-5 rounded-full border border-system-titanium",
              "flex items-center justify-center",
              "focus:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "cursor-pointer",
              className
            )}
            {...props}
          >
            <RadioGroupPrimitive.Indicator className="relative flex h-3 w-3">
              <div className="absolute inset-0 rounded-full bg-system-primary" />
            </RadioGroupPrimitive.Indicator>
          </RadioGroupPrimitive.Item>
        </div>

        <div className="space-y-1">
          <label
            htmlFor={id}
            className="cursor-pointer text-base font-medium text-system-crystalWhite"
          >
            {label}
          </label>
          {description && <p className="text-[15px] text-system-titanium">{description}</p>}
        </div>
      </div>
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
